// src/pages/SkuCreatePage.tsx
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Card, Divider, Input, Select, Space, Switch, Typography, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import CustomButton from "src/components/common/CustomButton";
import { snackbarStore } from "src/store/snackbarStore";
import { createSkuApi, createSkuMappingApi } from "src/api/sku/skuApi";
import type { SkuType } from "src/types/sku";
import ProductVariantSelectModal from "src/components/sku/ProductVariantSelectModal";
import AppInputNumber from "src/components/common/AppInputNumber";
import AppInput from "src/components/common/AppInput";
import { getWarehouseListApi, type WarehouseListItem } from "src/api/warehouse/warehouseApi";

const toast = (message: string, severity: "success" | "error" | "info" | "warning" = "info") =>
  snackbarStore.getState().addSnackbar({ message, severity });

export default function SkuCreatePage() {
  const navigate = useNavigate();

  const [skuCodeValue, setSkuCodeValue] = React.useState("");
  const [skuNameValue, setSkuNameValue] = React.useState("");
  const [skuDescriptionValue, setSkuDescriptionValue] = React.useState("");
  const [skuTypeValue, setSkuTypeValue] = React.useState<SkuType>("PRODUCT");
  const [barcodeValue, setBarcodeValue] = React.useState("");
  const [modelNumberValue, setModelNumberValue] = React.useState("");
  const [skuCategoryValue, setSkuCategoryValue] = React.useState("");
  const [manufacturerValue, setManufacturerValue] = React.useState("");
  const [originCountryValue, setOriginCountryValue] = React.useState("");
  const [weightValue, setWeightValue] = React.useState(0);
  const [lengthValue, setLengthValue] = React.useState(0);
  const [widthValue, setWidthValue] = React.useState(0);
  const [heightValue, setHeightValue] = React.useState(0);
  const [isActiveValue, setIsActiveValue] = React.useState(true);
  const [warehouseId, setWarehouseId] = React.useState<number | undefined>(undefined);

  const [warehouseList, setWarehouseList] = React.useState<WarehouseListItem[]>([]);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [productModalOpen, setProductModalOpen] = React.useState(false);
  const [selectedProducts, setSelectedProducts] = React.useState<
    Array<{ productId: number; productCode: string; productName: string; quantityPerOrder: number }>
  >([]);

  // Warehouse 목록 로드
  React.useEffect(() => {
    getWarehouseListApi({ page: 0, size: 1000, order: "warehouseName,asc" })
      .then((res) => {
        if (res.statusCode === 1 && res.data) {
          setWarehouseList(res.data.content);
        }
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async () => {
    if (!skuCodeValue.trim()) {
      setErrorMessage("SKU 코드는 필수입니다.");
      return;
    }
    if (!skuNameValue.trim()) {
      setErrorMessage("SKU명은 필수입니다.");
      return;
    }
    if (!skuTypeValue) {
      setErrorMessage("SKU 타입은 필수입니다.");
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    try {
      const payload = {
        skuCode: skuCodeValue.trim(),
        skuName: skuNameValue.trim(),
        skuDescription: skuDescriptionValue.trim(),
        skuType: skuTypeValue,
        barcode: barcodeValue.trim(),
        modelNumber: modelNumberValue.trim(),
        skuCategory: skuCategoryValue.trim(),
        manufacturer: manufacturerValue.trim(),
        originCountry: originCountryValue.trim(),
        weight: weightValue,
        length: lengthValue,
        width: widthValue,
        height: heightValue,
        warehouseId: warehouseId,
        isActive: isActiveValue,
      };

      const res = await createSkuApi(payload);
      const code = (res as any)?.statusCode ?? (res as any)?.status_code;
      if (code === 0 || code === 1) {
        const createdSku = (res as any)?.data;
        const createdSkuId = createdSku?.id;

        // 선택된 상품이 있으면 연결
        if (createdSkuId && selectedProducts.length > 0) {
          try {
            for (const product of selectedProducts) {
              await createSkuMappingApi({
                skuId: createdSkuId,
                productId: product.productId,
                quantityPerOrder: product.quantityPerOrder,
              });
            }
            toast(`SKU가 등록되었습니다. (${selectedProducts.length}개 상품 연결됨)`, "success");
          } catch (mappingError: any) {
            toast(
              `SKU는 등록되었으나 상품 연결 중 오류가 발생했습니다: ${mappingError?.message}`,
              "warning"
            );
          }
        } else {
          toast("SKU가 등록되었습니다.", "success");
        }
        navigate("/sku");
      } else {
        setErrorMessage((res as any)?.message || "SKU 생성 실패");
      }
    } catch (e: any) {
      setErrorMessage(e?.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveProduct = (productId: number) => {
    setSelectedProducts((prev) => prev.filter((p) => p.productId !== productId));
  };

  const productColumns: ColumnsType<(typeof selectedProducts)[0]> = [
    {
      title: "상품 ID",
      dataIndex: "productId",
      width: 100,
      align: "right",
    },
    {
      title: "상품코드",
      dataIndex: "productCode",
      width: 150,
      align: "left",
    },
    {
      title: "상품명",
      dataIndex: "productName",
      width: 300,
      align: "left",
    },
    {
      title: "주문당 출고수량",
      dataIndex: "quantityPerOrder",
      width: 150,
      align: "right",
    },
    {
      title: "삭제",
      width: 80,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <CustomButton
          size="small"
          customVariant="danger"
          onClick={() => handleRemoveProduct(record.productId)}
        >
          삭제
        </CustomButton>
      ),
    },
  ];

  return (
    <div style={{ padding: 24, width: "100%" }}>
      <Card>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <Typography.Title level={4} style={{ margin: 0 }}>
            SKU 등록
          </Typography.Title>
          <Space>
            <CustomButton variant="outlined" onClick={() => navigate("/sku")}>
              취소
            </CustomButton>
            <CustomButton variant="contained" onClick={handleSubmit} disabled={submitting}>
              {submitting ? "저장 중..." : "저장"}
            </CustomButton>
          </Space>
        </div>

        <Divider />

        {errorMessage && (
          <Alert type="error" description={errorMessage} style={{ marginBottom: 16 }} />
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* 기본 정보 */}
          <div>
            <Typography.Text strong style={{ fontSize: 15, display: "block", marginBottom: 12 }}>
              기본 정보
            </Typography.Text>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
              <div>
                <Typography.Text type="secondary">SKU 코드 *</Typography.Text>
                <AppInput value={skuCodeValue} onChange={(e) => setSkuCodeValue(e.target.value)} placeholder="코드" />
              </div>
              <div>
                <Typography.Text type="secondary">SKU명 *</Typography.Text>
                <AppInput value={skuNameValue} onChange={(e) => setSkuNameValue(e.target.value)} placeholder="상품명" />
              </div>
              <div>
                <Typography.Text type="secondary">SKU 타입 *</Typography.Text>
                <Select<SkuType>
                  value={skuTypeValue}
                  onChange={(value) => setSkuTypeValue(value)}
                  options={[{ label: "PRODUCT", value: "PRODUCT" }]}
                  style={{ width: "100%" }}
                />
              </div>
              <div>
                <Typography.Text type="secondary">창고</Typography.Text>
                <Select
                  value={warehouseId}
                  onChange={(value) => setWarehouseId(value)}
                  placeholder="창고 선택"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  style={{ width: "100%" }}
                  options={warehouseList.map((w) => ({
                    label: w.warehouseName,
                    value: w.warehouseId,
                  }))}
                />
              </div>
            </div>
            <div>
              <Typography.Text type="secondary">SKU 설명</Typography.Text>
              <Input.TextArea
                value={skuDescriptionValue}
                placeholder="설명"
                onChange={(e) => setSkuDescriptionValue(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <Divider />

          {/* 식별 정보 */}
          <div>
            <Typography.Text strong style={{ fontSize: 15, display: "block", marginBottom: 12 }}>
              식별 정보
            </Typography.Text>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
              <div>
                <Typography.Text type="secondary">바코드</Typography.Text>
                <AppInput value={barcodeValue} onChange={(e) => setBarcodeValue(e.target.value)} placeholder="바코드" />
              </div>
              <div>
                <Typography.Text type="secondary">모델 번호</Typography.Text>
                <AppInput
                  value={modelNumberValue}
                  onChange={(e) => setModelNumberValue(e.target.value)}
                  placeholder="모델번호"
                />
              </div>
              <div>
                <Typography.Text type="secondary">SKU 카테고리</Typography.Text>
                <AppInput
                  value={skuCategoryValue}
                  onChange={(e) => setSkuCategoryValue(e.target.value)}
                  placeholder="카테고리"
                />
              </div>
              <div>
                <Typography.Text type="secondary">제조사</Typography.Text>
                <AppInput
                  value={manufacturerValue}
                  onChange={(e) => setManufacturerValue(e.target.value)}
                  placeholder="제조사"
                />
              </div>
              <div>
                <Typography.Text type="secondary">원산지</Typography.Text>
                <AppInput
                  value={originCountryValue}
                  onChange={(e) => setOriginCountryValue(e.target.value)}
                  placeholder="원산지"
                />
              </div>
            </div>
          </div>

          <Divider />

          {/* 치수 정보 */}
          <div>
            <Typography.Text strong style={{ fontSize: 15, display: "block", marginBottom: 12 }}>
              치수 정보
            </Typography.Text>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
              <div>
                <Typography.Text type="secondary">무게</Typography.Text>
                <AppInputNumber
                  style={{ width: "100%" }}
                  value={weightValue}
                  onChange={(val) => setWeightValue(val || 0)}
                  placeholder="g"
                  min={0}
                />
              </div>
              <div>
                <Typography.Text type="secondary">길이</Typography.Text>
                <AppInputNumber
                  style={{ width: "100%" }}
                  value={lengthValue}
                  onChange={(val) => setLengthValue(val || 0)}
                  placeholder="mm"
                  min={0}
                />
              </div>
              <div>
                <Typography.Text type="secondary">너비</Typography.Text>
                <AppInputNumber
                  style={{ width: "100%" }}
                  value={widthValue}
                  onChange={(val) => setWidthValue(val || 0)}
                  placeholder="mm"
                  min={0}
                />
              </div>
              <div>
                <Typography.Text type="secondary">높이</Typography.Text>
                <AppInputNumber
                  style={{ width: "100%" }}
                  value={heightValue}
                  onChange={(val) => setHeightValue(val || 0)}
                  placeholder="mm"
                  min={0}
                />
              </div>
            </div>
          </div>

          <Divider />

          {/* 기타 설정 */}
          <div>
            <Typography.Text strong style={{ fontSize: 15, display: "block", marginBottom: 12 }}>
              기타 설정
            </Typography.Text>
            <Space size={8} align="center">
              <Switch checked={isActiveValue} onChange={(checked) => setIsActiveValue(checked)} />
              <Typography.Text>활성 여부</Typography.Text>
            </Space>
          </div>

          <Divider />

          {/* 연결상품 */}
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <Typography.Text strong style={{ fontSize: 15 }}>
                연결상품
              </Typography.Text>
              <CustomButton onClick={() => setProductModalOpen(true)}>상품 불러오기</CustomButton>
            </div>
            <Alert
              type="info"
              description="연결된 상품의 주문이 발생하면, 출고시에 해당 SKU가 출고됩니다."
              style={{ marginBottom: 12 }}
            />
            <Table
              columns={productColumns}
              dataSource={selectedProducts}
              rowKey="productId"
              pagination={false}
              scroll={{ x: 1000 }}
              locale={{ emptyText: "상품을 선택해주세요." }}
            />
          </div>
        </div>
      </Card>

      {/* 상품 선택 모달 */}
      <ProductVariantSelectModal
        open={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        onSelectProducts={(products) => {
          setSelectedProducts(products);
          toast(`${products.length}개의 상품이 선택되었습니다.`, "success");
        }}
      />
    </div>
  );
}
